import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Divider,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  Info,
  History,
  CheckCircle,
  Cancel,
  Person,
  QrCode,
  ExpandMore,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { closeDialog, openDialog } from '../../store/slices/uiSlice';
import { InventoryItem, CheckInEvent } from '../../types/models';
import { FirestoreService } from '../../firebase/firestoreService';
import { format } from 'date-fns';

const EVENTS_PAGE_SIZE = 10;

const ItemDetailDialog: React.FC = () => {
  const dispatch = useDispatch();
  const { dialog } = useSelector((state: RootState) => state.ui);

  const item = dialog.data as InventoryItem;
  const isOpen = dialog.open && dialog.type === 'item-detail';

  const [events, setEvents] = useState<CheckInEvent[]>([]);
  const [visibleCount, setVisibleCount] = useState(EVENTS_PAGE_SIZE);
  const [loadingEvents, setLoadingEvents] = useState(false);

  // Subscribe to real-time events for this item
  useEffect(() => {
    if (!isOpen || !item?.id) {
      setEvents([]);
      setVisibleCount(EVENTS_PAGE_SIZE);
      return;
    }

    setLoadingEvents(true);
    const unsubscribe = FirestoreService.subscribeToItemEvents(item.id, (itemEvents) => {
      setEvents(itemEvents);
      setLoadingEvents(false);
    });

    return () => unsubscribe();
  }, [isOpen, item?.id]);

  const handleClose = () => {
    dispatch(closeDialog());
  };

  const handleCheckInOut = () => {
    if (item.checkedOut) {
      dispatch(openDialog({ type: 'checkin', data: item }));
    } else {
      dispatch(openDialog({ type: 'checkout', data: item }));
    }
  };

  const getStatusColor = (checkedOut: boolean) => {
    return checkedOut ? 'error' : 'success';
  };

  const getStatusIcon = (checkedOut: boolean) => {
    return checkedOut ? <Cancel /> : <CheckCircle />;
  };

  const getCurrentLocation = () => {
    if (item?.checkedOut) {
      let checkedOutTo = item.checkedOutTo;

      // Fallback: check events or legacy checkOutHistory
      if (!checkedOutTo && events.length > 0) {
        const lastCheckoutEvent = events.find(e => e.checkedOut);
        checkedOutTo = lastCheckoutEvent?.userName;
      } else if (!checkedOutTo && item.checkOutHistory?.length > 0) {
        const lastCheckoutEvent = item.checkOutHistory
          .filter(event => event.checkedOut)
          .sort((a, b) => b.eventDate - a.eventDate)[0];
        checkedOutTo = lastCheckoutEvent?.userName;
      }

      return checkedOutTo ? `Checked out to ${checkedOutTo}` : 'Checked out';
    } else {
      return 'In Inventory';
    }
  };

  if (!isOpen || !item) {
    return null;
  }

  // Use Firestore events if available, fall back to legacy checkOutHistory
  const displayEvents = events.length > 0
    ? events
    : (item.checkOutHistory
        ? [...item.checkOutHistory].sort((a, b) => b.eventDate - a.eventDate)
        : []);
  const visibleEvents = displayEvents.slice(0, visibleCount);
  const hasMore = displayEvents.length > visibleCount;

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Info color="primary" />
          <Box>
            <Typography variant="h6">{item.itemName}</Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Item Details & History
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Item Information */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Item Information
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Chip
              label={item.checkedOut ? 'Checked Out' : 'Available'}
              color={getStatusColor(item.checkedOut)}
              icon={getStatusIcon(item.checkedOut)}
            />
            {item.itemGroup && (
              <Chip
                label={item.itemGroup}
                variant="outlined"
              />
            )}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Item ID
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {item.itemID}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="body2" color="text.secondary">
                Location
              </Typography>
              <Typography variant="body1" fontWeight="medium" color={item.checkedOut ? 'warning.main' : 'success.main'}>
                {getCurrentLocation()}
              </Typography>
            </Box>

            {item.itemQRFileName && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  QR Code
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <QrCode fontSize="small" />
                  <Typography variant="body1">
                    Available
                  </Typography>
                </Box>
              </Box>
            )}

            {item.checkedOutDate && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Checked Out Date
                </Typography>
                <Typography variant="body1">
                  {format(new Date(item.checkedOutDate * 1000), 'MMM d, yyyy h:mm a')}
                </Typography>
              </Box>
            )}

            {item.checkedOut && events.length > 0 && (() => {
              const lastCheckoutEvent = events.find(e => e.checkedOut);
              return lastCheckoutEvent?.notes ? (
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography variant="body2" color="text.secondary">
                    Check Out Notes
                  </Typography>
                  <Typography variant="body1">
                    {lastCheckoutEvent.notes}
                  </Typography>
                </Box>
              ) : null;
            })()}
          </Box>
        </Paper>

        {/* History Section */}
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <History />
            <Typography variant="h6">
              Check-in/Check-out History
            </Typography>
          </Box>

          {loadingEvents ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : visibleEvents.length > 0 ? (
            <>
              <List>
                {visibleEvents.map((event, index) => (
                  <React.Fragment key={`${event.eventID || index}-${event.eventDate}`}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: event.checkedOut ? 'error.main' : 'success.main'
                          }}
                        >
                          {event.checkedOut ? <Cancel /> : <CheckCircle />}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" fontWeight="medium">
                              {event.checkedOut ? 'Checked Out' : 'Checked In'}
                            </Typography>
                            <Chip
                              size="small"
                              label={event.userName}
                              icon={<Person />}
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {format(new Date(event.eventDate * 1000), 'MMM d, yyyy h:mm a')}
                            </Typography>
                            {event.notes && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                Notes: {event.notes}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < visibleEvents.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
              {hasMore && (
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                  <Button
                    size="small"
                    startIcon={<ExpandMore />}
                    onClick={() => setVisibleCount(prev => prev + EVENTS_PAGE_SIZE)}
                  >
                    Load More ({displayEvents.length - visibleCount} remaining)
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                No history available for this item
              </Typography>
            </Box>
          )}
        </Paper>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Close
        </Button>
        <Button 
          onClick={handleCheckInOut}
          variant="contained"
          color={item?.checkedOut ? 'success' : 'primary'}
          startIcon={item?.checkedOut ? <CheckCircle /> : <Cancel />}
        >
          {item?.checkedOut ? 'Check In' : 'Check Out'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemDetailDialog;